import React from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { ArrowRight, CircleCheck, FileText, FileX2, TriangleAlert } from "lucide-react";
import Status from "components/common_components/Status";
import AttorneySelect from "./AttorneySelect";
import FileCaseSelect from "./FileCaseSelect";
import { ReviewKind } from "../utils/copyToVersion";

const VersionCard = ({ form, current, caption }) => (
    <div className={clsx("cw-version", { "cw-version--current": current })}>
        <div className="cw-version__eyebrow">{current ? "To" : "From"}</div>
        <div className="cw-version__title">
            {form.code} <Status id={current ? "active" : "oldVersion"} />
        </div>
        <div className="cw-version__version">Version {form.version}</div>
        <div className="cw-version__caption">{caption}</div>
    </div>
);

const StatCard = ({ icon, value, label, hint, tone }) => (
    <div className={clsx("cw-stat", `cw-stat--${tone}`)}>
        <div className="cw-stat__icon">{icon}</div>
        <div className="cw-stat__value">{value}</div>
        <div className="cw-stat__label">{label}</div>
        <div className="cw-stat__hint">{hint}</div>
    </div>
);

/** First step: what is copied where, and the outcome of the copy at a glance. */
const CopyOverviewStep = ({
    sourceFormClient,
    targetForm,
    copyResult,
    addendumsCount,
    attorneyOptions,
    attorneyUserId,
    onAttorneyChange,
    fileCase,
    onFileCaseChange,
}) => {
    const { reviewItems, copiedCount } = copyResult;
    const tooLongCount = reviewItems.filter((item) => item.kind === ReviewKind.TooLong).length;
    const notCopiedCount = reviewItems.length - tooLongCount;

    return (
        <div className="cw-overview">
            <div className="cw-versions">
                <VersionCard form={sourceFormClient.form} caption="Stays as it is." />
                <ArrowRight className="cw-versions__arrow" size={28} />
                <VersionCard form={targetForm} current caption="A new form is created here, with status Open." />
            </div>

            <div className="form-head cw-details">
                <div className="item">
                    <b>Client:</b>
                    <span>{sourceFormClient.client?.fullName}</span>
                </div>
                <AttorneySelect
                    value={attorneyOptions.find((x) => x.id === attorneyUserId) || null}
                    options={attorneyOptions}
                    onChange={(attorneyUser) => onAttorneyChange(attorneyUser?.id || null)}
                />
                <FileCaseSelect clientId={sourceFormClient.clientId} value={fileCase} onChange={onFileCaseChange} />
            </div>

            <div className="cw-stats">
                <StatCard
                    tone="success"
                    icon={<CircleCheck size={22} />}
                    value={copiedCount - tooLongCount}
                    label="Values copied"
                    hint="Same field in the new version. Shown in green."
                />
                <StatCard
                    tone="warning"
                    icon={<TriangleAlert size={22} />}
                    value={tooLongCount}
                    label="Copied, but too long"
                    hint="Copied in full, but the new field shows only part of it. Shown in amber."
                />
                <StatCard
                    tone="error"
                    icon={<FileX2 size={22} />}
                    value={notCopiedCount}
                    label="Not copied"
                    hint="The field no longer exists, or the value is not valid there. Shown in red."
                />
                <StatCard
                    tone="neutral"
                    icon={<FileText size={22} />}
                    value={addendumsCount}
                    label="Addendums"
                    hint="Copied as they are. Their pages are matched against the new version."
                />
            </div>

            <p className="cw-note">
                Next you will see this form as it is now (version {sourceFormClient.form.version}), with every value marked by
                what happens to it. Nothing is saved until you agree in the last step.
            </p>
        </div>
    );
};

const formShape = PropTypes.shape({ code: PropTypes.string, version: PropTypes.string });

VersionCard.propTypes = {
    form: formShape.isRequired,
    current: PropTypes.bool,
    caption: PropTypes.string.isRequired,
};

VersionCard.defaultProps = { current: false };

StatCard.propTypes = {
    icon: PropTypes.node.isRequired,
    value: PropTypes.number.isRequired,
    label: PropTypes.string.isRequired,
    hint: PropTypes.string.isRequired,
    tone: PropTypes.oneOf(["success", "warning", "error", "neutral"]).isRequired,
};

CopyOverviewStep.propTypes = {
    sourceFormClient: PropTypes.shape({
        clientId: PropTypes.number,
        client: PropTypes.shape({ fullName: PropTypes.string }),
        form: formShape,
    }).isRequired,
    targetForm: formShape.isRequired,
    copyResult: PropTypes.shape({
        reviewItems: PropTypes.array.isRequired,
        copiedCount: PropTypes.number.isRequired,
    }).isRequired,
    addendumsCount: PropTypes.number.isRequired,
    attorneyOptions: PropTypes.array.isRequired,
    attorneyUserId: PropTypes.number,
    onAttorneyChange: PropTypes.func.isRequired,
    fileCase: PropTypes.shape({ module: PropTypes.string, fileCaseId: PropTypes.number, fileNo: PropTypes.string }),
    onFileCaseChange: PropTypes.func.isRequired,
};

CopyOverviewStep.defaultProps = {
    attorneyUserId: null,
    fileCase: null,
};

export default CopyOverviewStep;
